import React, {
  useEffect, useReducer, useRef, useState,
} from 'react';
import axios from 'axios';
import {
  Col, DatePicker, Grid, IconButton, Row, Stack, Table,
} from 'rsuite';
import { Icon } from '@rsuite/icons';
import { FaFileContract } from 'react-icons/fa';
import handleError from '../../../api/apiErrorHandle';
import { formatCarrier, loadShipments } from './alzaShipmentsService';
import { initialState, reducer } from './alzaShipmentsState';
import { Shipment } from '../../../api/orders/shipment/shipmentTypes';

export default function AlzaShipments(): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const controllerRef = useRef<AbortController | null>(null);

  async function fetchShipments(date: Date) {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    dispatch({ type: 'shipments/loading' });

    try {
      const shipments = await loadShipments(date, controller.signal);
      dispatch({ type: 'shipments/loaded', payload: shipments });
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }

      dispatch({ type: 'shipments/failed' });
      handleError(error);
    }
  }

  useEffect(() => {
    fetchShipments(selectedDate).then();

    return () => {
      controllerRef.current?.abort();
    };
  }, [selectedDate]);

  function handleDateChange(date: Date | null) {
    if (date) {
      setSelectedDate(date);
    }
  }

  return (
    <Grid fluid className="h100p">
      <Row>
        <Col xs={24}>
          <Stack spacing={6}>
            Shipments happening on:
            <DatePicker
              format="yyyy-MM-dd"
              size="lg"
              oneTap
              cleanable={false}
              value={selectedDate}
              onChange={date => handleDateChange(date)}
              ranges={[]}
            />
          </Stack>
        </Col>
      </Row>
      <Row className="table-container">
        <Col className="mt-20 h100p" xs={24}>
          <Table
            data={state.shipments}
            loading={state.status === 'loading'}
            rowKey="shippingListId"
            fillHeight
            bordered
          >
            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Branch</Table.HeaderCell>
              <Table.Cell dataKey="branch" />
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Carrier</Table.HeaderCell>
              <Table.Cell>
                {rowData => formatCarrier(rowData as Shipment)}
              </Table.Cell>
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Shipping list id</Table.HeaderCell>
              <Table.Cell dataKey="shippingListId" />
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell>Number of orders</Table.HeaderCell>
              <Table.Cell dataKey="numberOfOrders" />
            </Table.Column>
            <Table.Column width={120} align="center">
              <Table.HeaderCell>Shipping list</Table.HeaderCell>
              <Table.Cell style={{ padding: 8 }}>
                {rowData => {
                  const { shippingListUrl } = rowData as Shipment;
                  return shippingListUrl ? (
                    <IconButton
                      size="xs"
                      appearance="subtle"
                      title="Download shipping list"
                      icon={<Icon as={FaFileContract} />}
                      onClick={() => window.open(shippingListUrl)}
                    />
                  ) : null;
                }}
              </Table.Cell>
            </Table.Column>
          </Table>
        </Col>
      </Row>
    </Grid>
  );
}
